import {
  MACBOOKS_CATEGORIES,
  PC_LAPTOPS,
  ProductCategory,
} from '~/scopes/product-core/constants'
import type { BackboxGradeLabel } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.constants'

/**
 * Product categories where the review photos guidance feature is enabled.
 * The default grade images curated for the feature are smartphone-specific
 * (see `GRADE_DEFAULT_IMAGES_BY_CATEGORY`), so extend this list only when
 * matching curated images exist for the category.
 */
export const REVIEW_PHOTOS_GUIDANCE_CATEGORIES = [
  ProductCategory.SMARTPHONE,
  ...MACBOOKS_CATEGORIES,
  ...PC_LAPTOPS,
]

/**
 * Default grade images for MacBooks and laptops. Premium and flawless share
 * the same curated image.
 */
const MACBOOK_GRADE_IMAGES: Record<BackboxGradeLabel, string> = {
  PREMIUM: '/img/product/funnel/macbook/grades/excellent_premium.png',
  FLAWLESS: '/img/product/funnel/macbook/grades/excellent_premium.png',
  VERY_GOOD: '/img/product/funnel/macbook/grades/good.png',
  ECO: '/img/product/funnel/macbook/grades/fair.png',
}

/**
 * Default fallback image for each grade label, keyed by product category, used
 * as the first slide in the carousel when review photos are available. Only
 * categories with curated default images are listed.
 */
export const GRADE_DEFAULT_IMAGES_BY_CATEGORY: Partial<
  Record<string, Record<BackboxGradeLabel, string>>
> = {
  [ProductCategory.SMARTPHONE]: {
    PREMIUM: '/img/product/funnel/smartphone/grades/premium.png',
    FLAWLESS: '/img/product/funnel/smartphone/grades/excellent.png',
    VERY_GOOD: '/img/product/funnel/smartphone/grades/good.png',
    ECO: '/img/product/funnel/smartphone/grades/fair.png',
  },
  ...Object.fromEntries(
    [...MACBOOKS_CATEGORIES, ...PC_LAPTOPS].map((categoryId) => [
      categoryId,
      MACBOOK_GRADE_IMAGES,
    ]),
  ),
}

/**
 * Categories without curated default images fall back to the smartphone set.
 */
export const DEFAULT_GRADE_IMAGE_CATEGORY = ProductCategory.SMARTPHONE

/**
 * Numeric backbox grade values keyed by label. Used to match the grade value
 * from the URL (`useUrlParams().grade`) to the corresponding grade label.
 */
export const GRADE_VALUE_BY_LABEL: Record<BackboxGradeLabel, number> = {
  PREMIUM: 9,
  FLAWLESS: 10,
  VERY_GOOD: 11,
  ECO: 12,
}

export const GRADE_LABEL_BY_VALUE: Record<number, BackboxGradeLabel> =
  Object.fromEntries(
    (
      Object.entries(GRADE_VALUE_BY_LABEL) as Array<[BackboxGradeLabel, number]>
    ).map(([label, value]) => [value, label]),
  )
